import React, { useCallback, useEffect, useRef, useState } from 'react';
import store from './journalStore';

/* 🎙️ Voice notes — record straight into the vault.
   Audio is captured with MediaRecorder (webm/opus on Android & desktop,
   mp4/AAC on iPhones — whatever the device speaks), stored like photos:
   bytes in a blob, metadata in an index, served back only with the passcode.
   Recordings auto-stop at 3 minutes; the serverless cap is ~8MB. */

const MAX_SECONDS = 180;

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

/* a single playable clip (also used by the Timeline) */
export function VoiceClip({ passcode, v, onDelete }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let gone = false;
    let made = null;
    (async () => {
      try {
        const u = await store.voiceUrl(passcode, v.id);
        if (gone) {
          if (u && u.startsWith('blob:')) URL.revokeObjectURL(u);
          return;
        }
        made = u;
        setUrl(u);
      } catch (e) {
        if (!gone) setFailed(true);
      }
    })();
    return () => {
      gone = true;
      if (made && made.startsWith('blob:')) URL.revokeObjectURL(made);
    };
  }, [passcode, v.id]);

  return (
    <div className={`aj-vc aj-author-${(v.author || '').toLowerCase()}`}>
      <div className="aj-vc-top">
        <span className="aj-entry-author">🎙️ {v.author}</span>
        {v.dur > 0 && <span className="aj-entry-time">{fmt(v.dur)}</span>}
        {onDelete && (
          <button type="button" className="aj-del" onClick={() => onDelete(v.id)} aria-label="delete voice note">
            ×
          </button>
        )}
      </div>
      {failed ? (
        <div className="aj-muted">couldn&rsquo;t load this one 💔</div>
      ) : url ? (
        <audio className="aj-vc-audio" controls preload="metadata" src={url} />
      ) : (
        <div className="aj-vc-loading">loading…</div>
      )}
      {v.caption && <div className="aj-vc-cap">{v.caption}</div>}
    </div>
  );
}

/* recorder + list for one journal day */
export default function VoiceNotes({ passcode, date, me, onChanged }) {
  const [list, setList] = useState([]);
  const [recState, setRecState] = useState('idle'); // idle | recording | preview | saving
  const [elapsed, setElapsed] = useState(0);
  const [caption, setCaption] = useState('');
  const [err, setErr] = useState('');
  const [preview, setPreview] = useState(null); // { blob, url, dur, mime }

  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(0);
  const startedRef = useRef(0);

  const supported =
    typeof navigator !== 'undefined' &&
    !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) &&
    typeof window !== 'undefined' &&
    !!window.MediaRecorder;

  const load = useCallback(async () => {
    try {
      setList(await store.voices(passcode, date));
    } catch (e) {
      setList([]);
    }
  }, [passcode, date]);

  useEffect(() => {
    load();
  }, [load]);

  const cleanupStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    clearInterval(timerRef.current);
  };

  // never leave the mic running
  useEffect(
    () => () => {
      cleanupStream();
      if (preview && preview.url) URL.revokeObjectURL(preview.url);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const startRec = async () => {
    setErr('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      mrRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const mime = mr.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mime });
        cleanupStream();
        if (!blob.size) {
          setRecState('idle');
          setErr('Nothing got recorded — try again?');
          return;
        }
        if (blob.size > 7.5 * 1024 * 1024) {
          setRecState('idle');
          setErr('That one came out too large — keep it under ~3 minutes. 💕');
          return;
        }
        const dur = Math.round((Date.now() - startedRef.current) / 1000);
        setPreview({ blob, url: URL.createObjectURL(blob), dur, mime });
        setRecState('preview');
      };
      startedRef.current = Date.now();
      setElapsed(0);
      mr.start();
      setRecState('recording');
      timerRef.current = setInterval(() => {
        const s = Math.round((Date.now() - startedRef.current) / 1000);
        setElapsed(s);
        if (s >= MAX_SECONDS && mrRef.current && mrRef.current.state === 'recording') {
          mrRef.current.stop();
        }
      }, 250);
    } catch (e) {
      cleanupStream();
      setErr('Mic said no — check the microphone permission for this site. 🎙️');
    }
  };

  const stopRec = () => {
    if (mrRef.current && mrRef.current.state === 'recording') mrRef.current.stop();
  };

  const discard = () => {
    if (preview && preview.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
    setCaption('');
    setRecState('idle');
  };

  const save = async () => {
    if (!preview) return;
    setRecState('saving');
    setErr('');
    try {
      const base64 = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result).split(',')[1] || '');
        fr.onerror = reject;
        fr.readAsDataURL(preview.blob);
      });
      await store.addVoice(passcode, {
        date,
        author: me || 'Us',
        caption: caption.trim(),
        mime: preview.mime,
        dur: preview.dur,
        base64,
      });
      discard();
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setRecState('preview');
      setErr(e.message || 'Could not save the recording.');
    }
  };

  const del = async (id) => {
    try {
      await store.delVoice(passcode, id);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      /* ignore */
    }
  };

  return (
    <div className="aj-vn">
      <div className="aj-day-photos-head">
        <span className="aj-section-label">🎙️ Voice notes {list.length ? `(${list.length})` : ''}</span>
        {supported && recState === 'idle' && (
          <button type="button" className="aj-photo-add" onClick={startRec}>
            ● record
          </button>
        )}
        {recState === 'recording' && (
          <button type="button" className="aj-photo-add aj-vn-recording" onClick={stopRec}>
            <span className="aj-vn-dot" aria-hidden="true" /> {fmt(elapsed)} — stop ■
          </button>
        )}
      </div>

      {!supported && list.length === 0 && (
        <div className="aj-muted">this browser can&rsquo;t record audio — but recordings from other devices will play here. 💕</div>
      )}

      {recState === 'preview' && preview && (
        <div className="aj-vn-preview">
          <audio controls src={preview.url} className="aj-vc-audio" />
          <input
            type="text"
            className="aj-date-input aj-cap-input"
            value={caption}
            maxLength={140}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="caption (optional) — what's this one about? 💭"
          />
          <div className="aj-ev-formbtns">
            <button type="button" className="aj-btn-ghost aj-btn-ghost-sm" onClick={discard}>
              discard
            </button>
            <button type="button" className="aj-btn aj-btn-sm" onClick={save}>
              Save it 🎙️
            </button>
          </div>
        </div>
      )}
      {recState === 'saving' && <div className="aj-muted">saving your voice…</div>}
      {err && <div className="aj-err">{err}</div>}

      {list.length > 0 && (
        <div className="aj-vn-list">
          {list.map((v) => (
            <VoiceClip key={v.id} passcode={passcode} v={v} onDelete={del} />
          ))}
        </div>
      )}
    </div>
  );
}
